import * as _ from 'underscore'

import { ColumnModel } from '../../modules/api/api.models'
import { ItemSwapperResult } from '../../modules/common/item-swapper'
import { ItemSwapperPopupOptions, IItemSwapperPopup } from '../../modules/common/item-swapper/'

import { OpDistributorHierarchy, OpDistributor, OpView, OpViewColumn } from '../../models/core'
import { Enums } from '../../models/common'
import { ColumnInfo } from '../../models/custom'

import { LocalizationHelper, TypeHelper, WhereClauseHelper } from '../../helpers'
import { DataProviderListResult } from '../../data-provider'

import { ReferenceTypeService, REFERENCE_TYPE_SERVICE_NAME } from '../reference-type'
import { UserSessionService, USER_SESSION_SERVICE_NAME } from '../user-session'
import { GridService, GRID_SERVICE_NAME } from '../grid'

import { DistributorService, DISTRIBUTOR_SERVICE_NAME } from './distributor.service'

//#region Public Types

export const DISTRIBUTOR_HIERARCHY_SERVICE_NAME = 'DistributorHierarchyService'
export const DISTRIBUTOR_HIERARCHY_SERVICE_PROVIDER_NAME = 'DistributorHierarchyServiceProvider'

//#endregion


export class DistributorHierarchyService {


    //#region Costs

    private static get LOG_PREFIX(): string { return '[SIMAX.Services.Distributor.Hierarchy]' }

    //#endregion


    //#region Ctor

    public constructor(
        private readonly distributorService: DistributorService,
        private readonly gridService: GridService,
        private readonly referenceTypeService: ReferenceTypeService,
        private readonly userSessionService: UserSessionService,
    ) { }

    //#endregion

    //#region Columns

    public getChildDistributorGridColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService
            .getColumnModelByCategory(
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                DistributorService.SIMAX_MODULE_NAME,
                {
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor
                }
            )
    }

    //#endregion

    public isDistributorHierarchyEnabled = (): JQueryPromise<boolean> => {
        return this.userSessionService
            .getDataProvider()
            .Operator
            .getOperatorSettings(this.userSessionService.getIdLoggedOperator(), [Enums.SimaxOperatorSetting.DistributorHierarchyEnabled])
            .then((result) => {
                return !!_.first(result);
            })
    }

    //#region Item Swapper

    public getChildDistributorItemSwapperOptions(distributor: OpDistributor): JQueryPromise<ItemSwapperPopupOptions<OpDistributor>> {
        const viewPromise: JQueryPromise<OpView> = this.userSessionService
            .getDataProvider().View
            .getActiveView(Enums.ReferenceType.IdDistributor)

        const columnPromise: JQueryPromise<ColumnModel[]> = this.gridService
            .getColumnModelByCategory(
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                DistributorService.SIMAX_MODULE_NAME,
                {
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
                }
            )

        return $
            .when<any>(viewPromise, columnPromise)
            .then((view: OpView, columns: ColumnModel[]): ItemSwapperPopupOptions<OpDistributor> => {
                let pkColumn: OpViewColumn = null

                if (view != null) {
                    pkColumn = view.getPrimaryKeyColumn()
                }

                return {
                    itemSwapperOptions: {
                        rightGridOptions: {
                            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__AVAILABLE, DistributorService.SIMAX_MODULE_NAME),

                            columnsDictionary: columns,

                            getDataSourceFilter: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
                                if (pkColumn != null) {
                                    customWhereClause = WhereClauseHelper.appendIn(customWhereClause, pkColumn.name, distributor.idDistributor, true)
                                }

                                return this.userSessionService.getDataProvider().Distributor.getDistributorFilter(columns, pageNbr, pageSize, customWhereClause)
                            },

                            dataBag: true,
                        },

                        leftGridOptions: {
                            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CURRENT, DistributorService.SIMAX_MODULE_NAME),

                            columnsDictionary: columns,
                        }
                    },

                    itemSwapperPopupTitle: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CHILDREN_EDITING, DistributorService.SIMAX_MODULE_NAME),

                    onInitialized: (itemSwapper: IItemSwapperPopup<OpDistributor>) => {
                        return itemSwapper
                    },

                }
            })
    }

    //#endregion

    //#region Save

    public saveChildDistributors(distributor: OpDistributor, result: ItemSwapperResult<OpDistributor>): JQueryPromise<OpDistributor> {
        const newChildDistributors: OpDistributor[] = result.addedToLeft.map((newChildDistributor: OpDistributor) => {
            const distributor: OpDistributor = new OpDistributor(null)

            distributor.idDistributor = newChildDistributor.idDistributor
            distributor.opState = Enums.OpChangeState.New

            return distributor
        })

        const removedChildDistributors: OpDistributor[] = result.addedToRight.map((removedChildDistributor: OpDistributor) => {
            const distributor: OpDistributor = new OpDistributor(null)

            distributor.idDistributor = removedChildDistributor.idDistributor
            distributor.opState = Enums.OpChangeState.Delete

            return distributor
        })

        const removedGroupIds: number[] = removedChildDistributors.map((removedChildDistributor: OpDistributor) => removedChildDistributor.idDistributor)

        const loadedGroups: OpDistributor[] = distributor.childDistributors
            .filter((distributor: OpDistributor) => {
                return !_.contains(removedGroupIds, distributor.idDistributor)
            })
            .map((loadedDistributor: OpDistributor) => {
                const distributor: OpDistributor = new OpDistributor(null)

                distributor.idDistributor = loadedDistributor.idObject
                distributor.opState = Enums.OpChangeState.Loaded

                return distributor
            })


        const objectToSave: OpDistributor = new OpDistributor(null)

        objectToSave.idDistributor = distributor.idDistributor
        objectToSave.opState = Enums.OpChangeState.Modified
        objectToSave.childDistributors = [...newChildDistributors, ...loadedGroups, ...removedChildDistributors]

        return this.distributorService.saveDistributor(objectToSave)
    }

    //#endregion

    //#region Tree List

    public getTreeListDataSource(columns: ColumnModel[]): JQueryPromise<any[]> {
        return $
            .when<any>(
                this.referenceTypeService.getReferenceObjects<OpDistributor>(Enums.ReferenceType.IdDistributor, {
                    filterOptions: {
                        columns: _.map(columns, (column: ColumnModel) => new ColumnInfo(column)),
                        customWhereClause: ""
                    }
                }),
                this.referenceTypeService.getDictionaryReferenceObjectList(Enums.ReferenceType.IdDistributorHierarchy)
            )
            .then((distributors: DataProviderListResult<OpDistributor>, distributorHierarchy: OpDistributorHierarchy[]) => {
                const treeData: any[] = []

                _.forEach(distributors.objects, (distributor: OpDistributor, index: number) => {
                    const hierarchy: OpDistributorHierarchy = _.find(distributorHierarchy, (hierarchy: OpDistributorHierarchy) => {
                        return hierarchy.idDistributor === distributor.idDistributor;
                    });

                    const treeItem = this.gridService.gridObjectFromIOpObject(distributor);

                    if (hierarchy != null) {
                        treeItem.idDistributorHierarchy = hierarchy.idDistributorHierarchy
                        treeItem.idDistributorHierarchyParent = hierarchy.idDistributorHierarchyParent
                    }
                    else {
                        treeItem.idDistributorHierarchy = -(index + 1);
                    }

                    this.attachSourceObject(distributor, treeItem, false)

                    treeData.push(treeItem);
                });

                return treeData;
            });
    }

    /**
     * Attaches the source business object onto the given grid object.
     */
    private attachSourceObject(sourceObject: OpDistributor, gridObject: any, skipDataSourceSerialization: boolean): void {
        if (skipDataSourceSerialization) {
            gridObject[GridService.SOURCE_OBJECT_FIELD_NAME] = sourceObject
        }
        else if (TypeHelper.isIJSONSerializable(sourceObject)) {
            gridObject[GridService.SOURCE_OBJECT_FIELD_NAME] = (sourceObject as any).toJSON()
        }
        else {
            //console.warn(this.LOG_STRING, 'The given source object is not IJSONSerializable. Unable to attach it to grid object.');

            gridObject[GridService.SOURCE_OBJECT_FIELD_NAME] = null
        }
    }

    //#endregion
}

export class DistributorHierarchyServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        DISTRIBUTOR_SERVICE_NAME,
        GRID_SERVICE_NAME,
        REFERENCE_TYPE_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            distributorService: DistributorService,
            gridService: GridService,
            referenceTypeService: ReferenceTypeService,
            userSessionService: UserSessionService,
        ) {
            return new DistributorHierarchyService(distributorService, gridService, referenceTypeService, userSessionService)
        }]

    //#endregion
}